"use strict";
/**
 * What a PER-LOCATION subscription's Stripe event means for the TENANT.
 *
 * Subscriptions created by `/onboarding/complete` are stamped
 * `{ type: 'location_billing', source: 'onboarding' }`. That combination fell
 * between two services: super-admin skipped everything tagged
 * `location_billing`, and location-billing-service returns early unless the
 * metadata also carries a `requestId`, which onboarding never sets. The result
 * was that a failed payment on a per-location subscription was handled by
 * NOBODY — no grace, no suspension, no email.
 *
 * This is the rule that closes that gap. It deliberately decides ONLY the three
 * frozen-contract billing fields (`status`, `graceUntil` and, by implication,
 * access). It never touches `stripeSubscriptionId`, `subscriptionCost` or
 * `priceId`: a tenant has ONE of each of those and MANY per-location
 * subscriptions, so letting whichever subscription fired last write them would
 * corrupt the tenant record.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.decideLocationBillingEffect = decideLocationBillingEffect;
const DAY_MS = 86400000;
function decideLocationBillingEffect(args) {
    // A suspension is never lifted, extended or replaced from here. Restoring
    // access is the dunning tick's job or a human's; doing it on a single
    // location's invoice would let one paid location revive a dead account.
    if (args.currentStatus === 'suspended') {
        return { setStatus: null, reason: 'tenant is suspended — only the dunning tick or an admin may change that' };
    }
    if (args.eventType === 'invoice.payment_failed') {
        // Keep a grace window that is already running: each failing location must
        // not push the deadline further out, or a tenant with several failing
        // locations would never reach suspension.
        const running = typeof args.existingGraceUntil === 'number'
            && args.existingGraceUntil > args.now;
        return {
            setStatus: 'grace',
            setGraceUntil: running ? args.existingGraceUntil : args.now + args.graceDays * DAY_MS,
            reason: running
                ? 'a location invoice failed; the existing grace window keeps running'
                : 'a location invoice failed; grace window opened',
        };
    }
    if (args.eventType === 'invoice.paid') {
        if (args.otherLocationsUnpaid) {
            return { setStatus: null, reason: 'a location paid, but another location is still unpaid' };
        }
        return {
            setStatus: 'active',
            setGraceUntil: null,
            reason: 'every location is paid; grace window cleared',
        };
    }
    if (args.eventType === 'customer.subscription.deleted') {
        // ONE location closing is not the tenant churning. The tenant-level
        // cancellation path is the only thing that may suspend an account.
        return { setStatus: null, reason: 'one location subscription ended; the tenant is unaffected' };
    }
    return { setStatus: null, reason: `no tenant-level effect for ${args.eventType}` };
}
//# sourceMappingURL=locationBillingEvents.js.map